import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Local database types (mirror cloud schema)
export interface LocalTransaction {
  id: string;
  category_id: string | null;
  type: 'income' | 'expense';
  amount: number;
  description: string | null;
  date: string;
  created_at: string;
  updated_at: string;
  synced?: boolean;
  deleted?: boolean;
}

export interface LocalCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  type: 'income' | 'expense';
  parent_id: string | null;
  created_at: string;
  synced?: boolean;
  deleted?: boolean;
}

export interface LocalTag {
  id: string;
  name: string;
  color: string;
  created_at: string;
  synced?: boolean;
  deleted?: boolean;
}

export interface LocalTransactionTag {
  id: string;
  transaction_id: string;
  tag_id: string;
  synced?: boolean;
  deleted?: boolean;
}

export interface LocalProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  currency: string | null;
  wallet_name: string | null;
  week_start: string | null;
  default_period: string | null;
  first_day_of_month: number | null;
  round_amounts: boolean | null;
  initial_balance: number | null;
  created_at: string;
  updated_at: string;
}

export interface LocalBudget {
  id: string;
  name: string | null;
  category_id: string | null;  // deprecated - kept for migration
  category_ids: string[];      // new multi-category support
  amount: number;
  month: number | null;
  year: number;
  period_type: string;
  is_recurring: boolean;       // whether budget repeats every month/year
  created_at: string;
  updated_at: string;
  synced?: boolean;
  deleted?: boolean;
}

export interface LocalNotificationSettings {
  id: string;
  budget_alerts: boolean;
  large_expense_alerts: boolean;
  large_expense_threshold: number;
  weekly_summary: boolean;
  monthly_summary: boolean;
  goal_progress: boolean;
  low_balance_alert: boolean;
  low_balance_threshold: number;
  push_enabled: boolean;
  created_at: string;
  updated_at: string;
}

// Database schema
interface AxisDBSchema extends DBSchema {
  transactions: {
    key: string;
    value: LocalTransaction;
    indexes: { 'by-date': string; 'by-category': string };
  };
  categories: {
    key: string;
    value: LocalCategory;
    indexes: { 'by-type': string };
  };
  tags: {
    key: string;
    value: LocalTag;
  };
  transactionTags: {
    key: string;
    value: LocalTransactionTag;
    indexes: { 'by-transaction': string; 'by-tag': string };
  };
  profile: {
    key: string;
    value: LocalProfile;
  };
  budgets: {
    key: string;
    value: LocalBudget;
    indexes: { 'by-category': string };
  };
  notificationSettings: {
    key: string;
    value: LocalNotificationSettings;
  };
}

const DB_NAME = 'AxisFinanceDB';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<AxisDBSchema>> | null = null;
let dbInstance: IDBPDatabase<AxisDBSchema> | null = null;

export function getDb(): Promise<IDBPDatabase<AxisDBSchema>> {
  // If we have a cached instance, check if it's still valid
  if (dbInstance) {
    try {
      // Test if connection is still open by checking objectStoreNames
      if (dbInstance.objectStoreNames.length > 0) {
        return Promise.resolve(dbInstance);
      }
    } catch {
      // Connection is closed, reset cache
      dbPromise = null;
      dbInstance = null;
    }
  }
  
  if (!dbPromise) {
    dbPromise = openDB<AxisDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Transactions store
        if (!db.objectStoreNames.contains('transactions')) {
          const txStore = db.createObjectStore('transactions', { keyPath: 'id' });
          txStore.createIndex('by-date', 'date');
          txStore.createIndex('by-category', 'category_id');
        }

        // Categories store
        if (!db.objectStoreNames.contains('categories')) {
          const catStore = db.createObjectStore('categories', { keyPath: 'id' });
          catStore.createIndex('by-type', 'type');
        }

        // Tags store
        if (!db.objectStoreNames.contains('tags')) {
          db.createObjectStore('tags', { keyPath: 'id' });
        }

        // Transaction tags store
        if (!db.objectStoreNames.contains('transactionTags')) {
          const ttStore = db.createObjectStore('transactionTags', { keyPath: 'id' });
          ttStore.createIndex('by-transaction', 'transaction_id');
          ttStore.createIndex('by-tag', 'tag_id');
        }

        // Profile store
        if (!db.objectStoreNames.contains('profile')) {
          db.createObjectStore('profile', { keyPath: 'id' });
        }

        // Budgets store
        if (!db.objectStoreNames.contains('budgets')) {
          const budgetStore = db.createObjectStore('budgets', { keyPath: 'id' });
          budgetStore.createIndex('by-category', 'category_id');
        }

        // Notification settings store
        if (!db.objectStoreNames.contains('notificationSettings')) {
          db.createObjectStore('notificationSettings', { keyPath: 'id' });
        }
      },
      blocked() {
        // Another tab has an older version open - we need to wait
        console.warn('Database blocked by another tab');
      },
      blocking() {
        // We're blocking another tab from upgrading - close our connection
        dbInstance?.close();
        dbInstance = null;
        dbPromise = null;
      },
      terminated() {
        // Browser unexpectedly closed the connection
        dbInstance = null;
        dbPromise = null;
      },
    }).then((db) => {
      dbInstance = db;
      return db;
    }).catch((error) => {
      // Reset cache on error so we can retry
      dbPromise = null;
      dbInstance = null;
      throw error;
    });
  }
  return dbPromise;
}

// Helper to generate UUID
export function generateId(): string {
  return crypto.randomUUID();
}

// Subcategory definitions for expense categories
const expenseSubcategories: Record<string, { name: string; icon: string }[]> = {
  'Жильё и быт': [
    { name: 'Аренда / Ипотека', icon: '🏠' },
    { name: 'Коммунальные услуги', icon: '💡' },
    { name: 'Электричество', icon: '⚡' },
    { name: 'Вода', icon: '💧' },
    { name: 'Интернет и связь', icon: '📶' },
    { name: 'Ремонт и обслуживание', icon: '🔧' },
  ],
  'Еда': [
    { name: 'Продукты', icon: '🛒' },
    { name: 'Кафе и рестораны', icon: '🍽️' },
    { name: 'Доставка еды', icon: '🛵' },
  ],
  'Транспорт': [
    { name: 'Топливо', icon: '⛽' },
    { name: 'Общественный транспорт', icon: '🚌' },
    { name: 'Такси', icon: '🚕' },
    { name: 'Парковка', icon: '🅿️' },
    { name: 'Обслуживание авто', icon: '🔧' },
  ],
  'Покупки': [
    { name: 'Одежда', icon: '👕' },
    { name: 'Электроника', icon: '📱' },
    { name: 'Товары для дома', icon: '🏡' },
  ],
  'Развлечения': [
    { name: 'Кино и театр', icon: '🎬' },
    { name: 'Подписки', icon: '📺' },
    { name: 'Игры', icon: '🎮' },
    { name: 'Хобби', icon: '🎨' },
  ],
  'Здоровье': [
    { name: 'Врач', icon: '👨‍⚕️' },
    { name: 'Лекарства', icon: '💊' },
    { name: 'Спорт и фитнес', icon: '🏋️' },
    { name: 'Страховка', icon: '🏥' },
  ],
  'Образование': [
    { name: 'Курсы', icon: '📖' },
    { name: 'Книги', icon: '📚' },
  ],
  'Работа и бизнес': [
    { name: 'Рабочие расходы', icon: '💼' },
    { name: 'Инструменты', icon: '🛠️' },
  ],
  'Финансы': [
    { name: 'Переводы', icon: '💸' },
    { name: 'Комиссии', icon: '💳' },
    { name: 'Кредиты', icon: '🏦' },
  ],
};

// Subcategory definitions for income categories
const incomeSubcategories: Record<string, { name: string; icon: string }[]> = {
  'Основные доходы': [
    { name: 'Зарплата', icon: '💵' },
    { name: 'Премия', icon: '🎉' },
  ],
  'Дополнительные доходы': [
    { name: 'Фриланс', icon: '💻' },
    { name: 'Подарки', icon: '🎁' },
  ],
  'Финансовые поступления': [
    { name: 'Дивиденды', icon: '📈' },
    { name: 'Проценты', icon: '🏦' },
  ],
  'Возмещения': [
    { name: 'Возврат налогов', icon: '🧾' },
    { name: 'Кэшбэк', icon: '💳' },
  ],
};

// Get default categories for new local users
export function getDefaultCategories(): Omit<LocalCategory, 'id' | 'created_at' | 'synced'>[] {
  const expenseCategories = [
    { name: 'Жильё и быт', icon: '🏠', color: '#14b8a6', type: 'expense' as const, parent_id: null },
    { name: 'Еда', icon: '🍔', color: '#f97316', type: 'expense' as const, parent_id: null },
    { name: 'Транспорт', icon: '🚗', color: '#8b5cf6', type: 'expense' as const, parent_id: null },
    { name: 'Покупки', icon: '🛒', color: '#3b82f6', type: 'expense' as const, parent_id: null },
    { name: 'Развлечения', icon: '🎉', color: '#ec4899', type: 'expense' as const, parent_id: null },
    { name: 'Здоровье', icon: '🏥', color: '#22c55e', type: 'expense' as const, parent_id: null },
    { name: 'Образование', icon: '📚', color: '#6366f1', type: 'expense' as const, parent_id: null },
    { name: 'Работа и бизнес', icon: '💼', color: '#64748b', type: 'expense' as const, parent_id: null },
    { name: 'Финансы', icon: '💳', color: '#ef4444', type: 'expense' as const, parent_id: null },
    { name: 'Прочее', icon: '📦', color: '#78716c', type: 'expense' as const, parent_id: null },
  ];

  const incomeCategories = [
    { name: 'Основные доходы', icon: '💰', color: '#22c55e', type: 'income' as const, parent_id: null },
    { name: 'Дополнительные доходы', icon: '🎁', color: '#f59e0b', type: 'income' as const, parent_id: null },
    { name: 'Финансовые поступления', icon: '🏦', color: '#3b82f6', type: 'income' as const, parent_id: null },
    { name: 'Возмещения', icon: '🧾', color: '#14b8a6', type: 'income' as const, parent_id: null },
    { name: 'Прочее', icon: '📦', color: '#78716c', type: 'income' as const, parent_id: null },
  ];

  return [...expenseCategories, ...incomeCategories];
}

// Get subcategories for a parent category
export function getDefaultSubcategories(parentName: string, parentType: 'income' | 'expense', parentColor: string): { name: string; icon: string; color: string; type: 'income' | 'expense' }[] {
  const subcats = parentType === 'expense' 
    ? expenseSubcategories[parentName] 
    : incomeSubcategories[parentName];
  
  if (!subcats) return [];
  
  return subcats.map(sub => ({
    ...sub,
    color: parentColor,
    type: parentType,
  }));
}

// Initialize local database with default data
export async function initializeLocalDatabase(): Promise<void> {
  const db = await getDb();
  
  // Check if profile exists
  const existingProfile = await db.get('profile', 'local');
  
  if (!existingProfile) {
    const now = new Date().toISOString();
    
    // Create default profile
    await db.put('profile', {
      id: 'local',
      display_name: null,
      avatar_url: null,
      currency: 'KZT',
      wallet_name: 'Личный',
      week_start: 'monday',
      default_period: 'month',
      first_day_of_month: 1,
      round_amounts: true,
      initial_balance: 0,
      created_at: now,
      updated_at: now,
    });

    // Create default notification settings
    await db.put('notificationSettings', {
      id: 'local',
      budget_alerts: true,
      large_expense_alerts: true,
      large_expense_threshold: 10000,
      weekly_summary: true,
      monthly_summary: true,
      goal_progress: true,
      low_balance_alert: false,
      low_balance_threshold: 5000,
      push_enabled: false,
      created_at: now,
      updated_at: now,
    });

    // Create default categories with subcategories (only if none exist)
    const existingCategories = await db.getAll('categories');
    
    if (existingCategories.length === 0) {
      const defaultCategories = getDefaultCategories();
      const tx = db.transaction('categories', 'readwrite');
      
      for (const cat of defaultCategories) {
        const parentId = generateId();
        
        // Add parent category
        await tx.store.put({
          ...cat,
          id: parentId,
          created_at: now,
          synced: false,
        });
        
        // Add subcategories
        const subcats = getDefaultSubcategories(cat.name, cat.type, cat.color);
        for (const sub of subcats) {
          await tx.store.put({
            ...sub,
            id: generateId(),
            parent_id: parentId,
            created_at: now,
            synced: false,
          });
        }
      }
      
      await tx.done;
    }
  } else {
    // Migration for existing users: add missing subcategories
    await migrateAddSubcategories();
  }
}

// Migration: add subcategories for existing local users who only have parent categories
export async function migrateAddSubcategories(): Promise<number> {
  const db = await getDb();
  const allCategories = await db.getAll('categories');
  const now = new Date().toISOString();
  
  // Get parent categories (no parent_id)
  const parentCategories = allCategories.filter(c => !c.parent_id && !c.deleted);
  
  let addedCount = 0;
  const tx = db.transaction('categories', 'readwrite');
  
  for (const parent of parentCategories) {
    // Check if this parent already has subcategories
    const existingSubs = allCategories.filter(c => c.parent_id === parent.id && !c.deleted);
    
    if (existingSubs.length === 0) {
      // Get default subcategories for this parent
      const subcats = getDefaultSubcategories(parent.name, parent.type, parent.color);
      
      for (const sub of subcats) {
        // Check if subcategory already exists (maybe as orphan)
        const existsAsOrphan = allCategories.some(
          c => c.name === sub.name && c.type === sub.type && !c.deleted
        );
        
        if (!existsAsOrphan) {
          await tx.store.put({
            ...sub,
            id: generateId(),
            parent_id: parent.id,
            created_at: now,
            synced: false,
          });
          addedCount++;
        }
      }
    }
  }
  
  await tx.done;
  return addedCount;
}
export async function deduplicateCategories(): Promise<number> {
  const db = await getDb();
  const allCategories = await db.getAll('categories');
  
  const seen = new Map<string, string>(); // key -> oldest id
  const duplicateIds: string[] = [];
  
  // Sort by created_at to keep oldest
  const sorted = [...allCategories].sort((a, b) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  
  for (const cat of sorted) {
    const key = `${cat.name}|${cat.type}|${cat.parent_id || ''}`;
    if (seen.has(key)) {
      duplicateIds.push(cat.id);
    } else {
      seen.set(key, cat.id);
    }
  }
  
  // Delete duplicates
  if (duplicateIds.length > 0) {
    const tx = db.transaction('categories', 'readwrite');
    for (const id of duplicateIds) {
      await tx.store.delete(id);
    }
    await tx.done;
  }
  
  return duplicateIds.length;
}

// Clear all local data
export async function clearLocalDatabase(): Promise<void> {
  const db = await getDb();
  
  await Promise.all([
    db.clear('transactions'),
    db.clear('categories'),
    db.clear('tags'),
    db.clear('transactionTags'),
    db.clear('profile'),
    db.clear('budgets'),
    db.clear('notificationSettings'),
  ]);
}

// Export all local data (for sync)
export async function exportLocalData() {
  const db = await getDb();
  
  const [allTransactions, allCategories, allTags, allTransactionTags, profile, allBudgets] = await Promise.all([
    db.getAll('transactions'),
    db.getAll('categories'),
    db.getAll('tags'),
    db.getAll('transactionTags'),
    db.get('profile', 'local'),
    db.getAll('budgets'),
  ]);

  return {
    transactions: allTransactions.filter(t => !t.deleted),
    categories: allCategories.filter(c => !c.deleted),
    tags: allTags.filter(t => !t.deleted),
    transactionTags: allTransactionTags.filter(tt => !tt.deleted),
    profile,
    budgets: allBudgets.filter(b => !b.deleted),
  };
}

// Helper functions for CRUD operations
export const localDb = {
  // Transactions
  transactions: {
    async getAll(): Promise<LocalTransaction[]> {
      const db = await getDb();
      const all = await db.getAll('transactions');
      return all.filter(t => !t.deleted).sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    },
    async get(id: string): Promise<LocalTransaction | undefined> {
      const db = await getDb();
      return db.get('transactions', id);
    },
    async add(transaction: LocalTransaction): Promise<void> {
      const db = await getDb();
      await db.put('transactions', transaction);
    },
    async update(id: string, updates: Partial<LocalTransaction>): Promise<void> {
      const db = await getDb();
      const existing = await db.get('transactions', id);
      if (existing) {
        await db.put('transactions', { ...existing, ...updates });
      }
    },
    async delete(id: string): Promise<void> {
      const db = await getDb();
      const existing = await db.get('transactions', id);
      if (existing) {
        await db.put('transactions', { ...existing, deleted: true, synced: false });
      }
    },
  },
  
  // Categories
  categories: {
    async getAll(): Promise<LocalCategory[]> {
      const db = await getDb();
      const all = await db.getAll('categories');
      return all.filter(c => !c.deleted);
    },
    async get(id: string): Promise<LocalCategory | undefined> {
      const db = await getDb();
      return db.get('categories', id);
    },
    async add(category: LocalCategory): Promise<void> {
      const db = await getDb();
      await db.put('categories', category);
    },
    async update(id: string, updates: Partial<LocalCategory>): Promise<void> {
      const db = await getDb();
      const existing = await db.get('categories', id);
      if (existing) {
        await db.put('categories', { ...existing, ...updates });
      }
    },
    async delete(id: string): Promise<void> {
      const db = await getDb();
      const existing = await db.get('categories', id);
      if (existing) {
        await db.put('categories', { ...existing, deleted: true, synced: false });
      }
    },
  },
  
  // Tags
  tags: {
    async getAll(): Promise<LocalTag[]> {
      const db = await getDb();
      const all = await db.getAll('tags');
      return all.filter(t => !t.deleted);
    },
    async get(id: string): Promise<LocalTag | undefined> {
      const db = await getDb();
      return db.get('tags', id);
    },
    async add(tag: LocalTag): Promise<void> {
      const db = await getDb();
      await db.put('tags', tag);
    },
    async update(id: string, updates: Partial<LocalTag>): Promise<void> {
      const db = await getDb();
      const existing = await db.get('tags', id);
      if (existing) {
        await db.put('tags', { ...existing, ...updates });
      }
    },
    async delete(id: string): Promise<void> {
      const db = await getDb();
      const existing = await db.get('tags', id);
      if (existing) {
        await db.put('tags', { ...existing, deleted: true, synced: false });
      }
    },
  },
  
  // Transaction tags
  transactionTags: {
    async getAll(): Promise<LocalTransactionTag[]> {
      const db = await getDb();
      const all = await db.getAll('transactionTags');
      return all.filter(tt => !tt.deleted);
    },
    async getByTransaction(transactionId: string): Promise<LocalTransactionTag[]> {
      const db = await getDb();
      const all = await db.getAllFromIndex('transactionTags', 'by-transaction', transactionId);
      return all.filter(tt => !tt.deleted);
    },
    async add(transactionTag: LocalTransactionTag): Promise<void> {
      const db = await getDb();
      await db.put('transactionTags', transactionTag);
    },
    async delete(id: string): Promise<void> {
      const db = await getDb();
      const existing = await db.get('transactionTags', id);
      if (existing) {
        await db.put('transactionTags', { ...existing, deleted: true });
      }
    },
  },
  
  // Profile
  profile: {
    async get(): Promise<LocalProfile | undefined> {
      const db = await getDb();
      return db.get('profile', 'local');
    },
    async update(updates: Partial<LocalProfile>): Promise<void> {
      const db = await getDb();
      const existing = await db.get('profile', 'local');
      if (existing) {
        await db.put('profile', { ...existing, ...updates, updated_at: new Date().toISOString() });
      }
    },
  },
  
  // Budgets
  budgets: {
    async getAll(): Promise<LocalBudget[]> {
      const db = await getDb();
      const all = await db.getAll('budgets');
      return all.filter(b => !b.deleted);
    },
    async add(budget: LocalBudget): Promise<void> {
      const db = await getDb();
      await db.put('budgets', budget);
    },
    async update(id: string, updates: Partial<LocalBudget>): Promise<void> {
      const db = await getDb();
      const existing = await db.get('budgets', id);
      if (existing) {
        await db.put('budgets', { ...existing, ...updates, updated_at: new Date().toISOString() });
      }
    },
    async delete(id: string): Promise<void> {
      const db = await getDb();
      const existing = await db.get('budgets', id);
      if (existing) {
        await db.put('budgets', { ...existing, deleted: true, synced: false });
      }
    },
  },
};
